import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

const C = {
  navy: '#0A1628', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD',
  borderLt: '#E8EBF0', text: '#0A0A0A', textSec: '#3D4552',
  muted: '#6B7280', mutedLt: '#9CA3AF',
};

interface Fund { id: string; name: string; asset_class: string; strategy: string; status: string; inception_date: string; }

const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund:     { color: '#0D6E8A', bg: '#EFF8FB' },
  pe:             { color: '#92640A', bg: '#FDF6E7' },
  vc:             { color: '#1D4ED8', bg: '#EFF4FF' },
  re:             { color: '#B91C1C', bg: '#FEF2F2' },
  credit:         { color: '#0F766E', bg: '#F0FDFA' },
  infrastructure: { color: '#6D28D9', bg: '#F5F3FF' },
};

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

export default function PeerAnalysisSelector() {
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firm) return;
    supabase.from('funds').select('id, name, asset_class, strategy, status, inception_date')
      .eq('firm_id', firm.id).order('created_at', { ascending: false })
      .then(({ data }) => { if (data) setFunds(data); setLoading(false); });
  }, [firm]);

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading...</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      <div style={{ marginBottom: '22px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          Peer Analysis
        </h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>
          Select a fund to view its anonymous peer comparison
        </p>
      </div>

      {funds.length === 0 ? (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '60px', textAlign: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: C.text, fontFamily: 'Georgia, serif', marginBottom: '6px' }}>No funds yet</div>
          <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px' }}>Create a fund first to access peer analysis</div>
          <button onClick={() => navigate('/create-fund')} style={{
            background: C.navy, border: 'none', borderRadius: '3px',
            color: C.white, fontSize: '12.5px', fontWeight: 600,
            padding: '9px 20px', cursor: 'pointer', fontFamily: 'system-ui',
          }}>Create Fund</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {funds.map(fund => {
            const cls = classColors[fund.asset_class] || { color: C.muted, bg: C.surface };
            return (
              <div key={fund.id} onClick={() => navigate(`/peer-analysis/${fund.id}`)}
                style={{
                  background: C.white, border: `1px solid ${C.border}`,
                  borderLeft: `3px solid ${cls.color}`,
                  borderRadius: '4px', padding: '16px 18px',
                  cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between',
                  transition: 'box-shadow 0.1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: C.navy }}>{fund.name}</span>
                    <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', background: cls.bg, color: cls.color, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      {classLabels[fund.asset_class] || fund.asset_class}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: C.muted }}>
                    {fund.strategy || '-'} · Inception {fund.inception_date ? new Date(fund.inception_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-'}
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: C.navy, fontWeight: 600, letterSpacing: '0.2px' }}>
                  View Analysis →
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}